import { useState, useRef, useCallback } from 'react';

export const SPIN_GENERIC = 'Loading...';
export const SPIN_SAVE_MSG = 'Saving Tax Return...';
export const SPIN_DISCARD_MSG = 'Discarding Tax Return...';
export const SPIN_LOAD_MSG = 'Loading Form...';
export const SPIN_LOCKED_CLOSE = 'Closing Tax Return...';
export const SPIN_LOCKED_DELETE = 'Deleting Tax Return...';

/**
 * useSpinner
 *
 * Owns the loading-spinner state that was previously kept in the FormViewer
 * class component as this.state.isLoading / this.state.loadingText, plus the
 * three related methods:
 *
 *   startSpin(loadingText?) — set isLoading true with optional message
 *   stopSpin()              — set isLoading false (no-op when already false)
 *   hideFormButton()        — hide the #buttonMenuForReturns DOM element
 *
 * Constants (also exported so callers can use the same strings):
 *   SPIN_GENERIC, SPIN_SAVE_MSG, SPIN_DISCARD_MSG,
 *   SPIN_LOAD_MSG, SPIN_LOCKED_CLOSE, SPIN_LOCKED_DELETE
 */
const useSpinner = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadingText, setLoadingText] = useState(SPIN_GENERIC);

  // Kept as a ref so stopSpin can read the latest value without stale closure.
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;

  const hideFormButton = useCallback(() => {
    const el = document.getElementById('buttonMenuForReturns');
    if (el) el.style.setProperty('display', 'none');
  }, []);

  const startSpin = useCallback(text => {
    const msg = !text || text === '' ? SPIN_GENERIC : text;
    setIsLoading(true);
    setLoadingText(msg);
  }, []);

  const stopSpin = useCallback(() => {
    if (isLoadingRef.current) {
      hideFormButton();
      setIsLoading(false);
    }
  }, [hideFormButton]);

  return { isLoading, loadingText, startSpin, stopSpin, hideFormButton };
};

export default useSpinner;
